import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  List,
  ListItemButton,
  Box,
  ListItemText,
  Drawer,
  Collapse
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Attention20Px,
  Batch24Px,
  BatchAdd24Px,
  Bin24Px,
  Cart20Px,
  ChartBar24Px,
  Code24Px,
  ConfirmationModal,
  Consol20Px,
  Controller20Px,
  GridDesignIcon,
  LiveData20Px,
  Modules20Px,
  R5Robot24Px,
  Requirements24Px,
  Service20Px,
  Settings24Px,
  Switch20Px,
  Warehouse20Px,
  Workstation24Px
} from "@qubit/autoparts";

import { SyntheticEvent, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link as RouterLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import envConstants from "~/config/envConstants";
import { useFlag } from "~/config/flags";
import { setPickedQuantityModalStatus } from "~/features/autostorePicking/confirmPickQuantityModal/confirmPickQuantityModal.slice";
import {
  setIsAdjustingBins,
  setSelectedSummaries
} from "~/features/inventory/inventory.slice";
import { useServiceSupportSectionGuards } from "~/features/serviceSupport/serviceSupport.routes";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useSideNav } from "~/hooks/useSideNav";
import {
  isAutostoreView,
  isWorkstationWithSinglePort,
  parentPortIdFoundInWorkstation
} from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackNavigationDrawerWrapper,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";

import {
  closePort,
  closeWorkstation,
  fetchPortStatus,
  setConfirmationModalVisibilityStatus
} from "~/redux/actions";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectUserCanViewSettings,
  selectUserIsAdmin,
  selectUsersClientId
} from "~/redux/selectors/authSelectors";
import { selectIsAdjustingBins } from "~/redux/selectors/inventorySelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import {
  selectIfWorkstationsTabShouldBeEnabled,
  selectSitePortId,
  selectThisWorkstation,
  selectWorkstationAvailablePorts
} from "~/redux/selectors/workstationsSelectors";

import { useLazyGetAutostoreGridStatusQuery } from "~/redux/warehouse/autostoreGrid.hooks";

import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

import {
  type NavDrawerEntry,
  type NavGroup,
  type NavGroupChild,
  navDrawerEntryType
} from "./navDrawer.types";
import {
  createNavDrawerAltIconRenderer,
  createNavDrawerDefaultIconRenderer,
  decorateNavDrawerLinkIcons
} from "./navDrawer.utils";
import { useSideNavSubmenuAutoExpand } from "./useSideNavSubmenuAutoExpand";

const AltBatchIcon = styled(Batch24Px)({
  "& path": {
    fill: "#185BD1"
  }
});

const AltBatchAddIcon = styled(BatchAdd24Px)({
  id: "alt-batch-add-icon",
  "& path": {
    fill: "#185BD1"
  }
});

const parseQueryParams = (search: string): Record<string, string> => {
  const queryParams = new URLSearchParams(search);
  const params: Record<string, string> = {};
  queryParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

/** Exact path or child path under `to` — avoids `includes` false positives (e.g. `/dev-tools/service-support-events-preview`, `/dev-tools/log-publisher-grid`, or `/dev-tools/icon-list` vs `/service-support`). */
function pathnameMatchesNavLinkTarget(pathname: string, to: string): boolean {
  const base = to.split("?")[0];
  return pathname === base || pathname.startsWith(`${base}/`);
}

export const drawerWidth = "216px";
export const navBarHeight = "48px";

export const NavigationDrawer = () => {
  const { t } = useTranslation();
  const { enableWorkstationBinMaintenance, enableSideNavSubMenus } = useFlag();

  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Navigate Away");

  const isAdjustingBins = useAppSelector(selectIsAdjustingBins);
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const usersClientId = useAppSelector(selectUsersClientId);
  const {
    manualOpsEnabled,
    binCleaningEnabled,
    dev_orderCreation,
    dev_ptlSimulation,
    dev_conveyanceSimulation
  } = useAppSelector(selectServerConfig);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const workstationAvailablePorts = useAppSelector(
    selectWorkstationAvailablePorts
  );
  const sitePortId = useAppSelector(selectSitePortId);
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const shouldEnableWorkstationTab = useAppSelector(
    selectIfWorkstationsTabShouldBeEnabled
  );

  const canUserViewSettings = useAppSelector(selectUserCanViewSettings);
  const isConfirmationModalOpen = useAppSelector(
    (state) => state.autostore.isConfirmationModalOpen
  );
  const { isAutostorePicking, isSideNavOpen, setIsSideNavOpen } = useSideNav();
  const selectedAutostoreGridId = siteWorkstation?.autostoreGridId;

  const isMaintenanceWorkstation =
    siteWorkstation?.roles.includes("Maintenance");
  const { pathname, search } = useLocation();

  const [groupExpanded, setGroupExpanded] = useSideNavSubmenuAutoExpand(
    pathname,
    enableSideNavSubMenus
  );

  const currentPagePathname = pathname.split("/").pop()?.toLowerCase();

  const [fetchAutostoreGridStatus] = useLazyGetAutostoreGridStatusQuery();

  const dispatch = useAppDispatch();
  const navigate = useFcNavigate();
  const queryParams = parseQueryParams(search);
  const isAutostoreQueryParamPresent = queryParams.autostore === "true";

  const [navigateToUrl, setNavigateToUrl] = useState<string>("");

  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";

  const showDevSection =
    dev_ptlSimulation ||
    dev_conveyanceSimulation ||
    dev_orderCreation ||
    areDevCheatsEnabled;

  const isSelectedBinCleaningWorkstation = useMemo(() => {
    if (!siteWorkstation) return false;
    return siteWorkstation.ports.some(
      (port) => port.configuration.toLowerCase() === "cleaning"
    );
  }, [siteWorkstation]);

  const settingsSubNavChildren: NavGroupChild[] = !enableSideNavSubMenus
    ? []
    : [
        {
          text: t("general"),
          to: "/settings/general",
          match: (p: string) => p === "/settings" || p === "/settings/general",
          icon: createNavDrawerDefaultIconRenderer(
            () => <Controller20Px />,
            t("general")
          ),
          altIcon: createNavDrawerAltIconRenderer(
            (color: string) => <Controller20Px fill={color} />,
            t("general")
          )
        },
        ...(shouldEnableWorkstationTab
          ? [
              {
                text: t("workstations"),
                to: "/settings/workstation",
                match: (p: string) => p === "/settings/workstation",
                icon: createNavDrawerDefaultIconRenderer(
                  () => <Workstation24Px />,
                  t("workstations")
                ),
                altIcon: createNavDrawerAltIconRenderer(
                  (color: string) => <Workstation24Px fill={color} />,
                  t("workstations")
                )
              } satisfies NavGroupChild
            ]
          : []),
        ...(isAdmin
          ? [
              {
                text: t("bins"),
                to: "/settings/bins",
                match: (p: string) => p === "/settings/bins",
                icon: createNavDrawerDefaultIconRenderer(
                  () => <Bin24Px />,
                  "bins"
                ),
                altIcon: createNavDrawerAltIconRenderer(
                  (color: string) => <Bin24Px fill={color} />,
                  "bins"
                )
              } satisfies NavGroupChild
            ]
          : []),
        ...(isAdmin
          ? [
              {
                text: t("batch"),
                to: "/settings/batches",
                match: (p: string) => p === "/settings/batches",
                icon: createNavDrawerDefaultIconRenderer(
                  () => <Batch24Px />,
                  "batch"
                ),
                altIcon: createNavDrawerAltIconRenderer(
                  (color: string) => <Batch24Px fill={color} />,
                  "batch"
                )
              } satisfies NavGroupChild
            ]
          : [])
      ];

  const devToolsDrawerDecorated = decorateNavDrawerLinkIcons({
    text: "Dev Tools",
    to: "/dev-tools",
    icon: () => <Code24Px />,
    altIcon: (color: string) => <Code24Px fill={color} />
  });

  const settingsDrawerDecorated = decorateNavDrawerLinkIcons({
    text: t("nav.link.settings"),
    to: "/settings",
    icon: () => <Settings24Px />,
    altIcon: (color: string) => <Settings24Px fill={color} />,
    additionalRoutes: ["order-creation"]
  });

  const {
    shouldRenderTasksTab,
    shouldDisplayAndonTab,
    shouldRenderConfigurationsTab,
    enableTestingToolsTab
  } = useServiceSupportSectionGuards();

  const serviceSupportSubNavChildren: NavGroupChild[] = useMemo(() => {
    if (!enableSideNavSubMenus) return [];

    return [
      {
        text: t("overview"),
        to: "/service-support",
        match: (p: string) => p === "/service-support",
        icon: createNavDrawerDefaultIconRenderer(
          () => <Modules20Px />,
          t("overview")
        ),
        altIcon: createNavDrawerAltIconRenderer(
          (color: string) => <Modules20Px fill={color} />,
          t("overview")
        )
      },
      {
        text: t("Cube Analytics"),
        to: "/service-support/cube-analytics",
        match: (p: string) => p === "/service-support/cube-analytics",
        icon: createNavDrawerDefaultIconRenderer(
          () => <ChartBar24Px />,
          t("Cube Analytics")
        ),
        altIcon: createNavDrawerAltIconRenderer(
          (color: string) => <ChartBar24Px fill={color} />,
          t("Cube Analytics")
        )
      },
      ...(shouldRenderConfigurationsTab
        ? [
            {
              text: t("configurations"),
              to: "/service-support/configurations",
              match: (p: string) => p === "/service-support/configurations",
              icon: createNavDrawerDefaultIconRenderer(
                () => <GridDesignIcon />,
                t("configurations")
              ),
              altIcon: createNavDrawerAltIconRenderer(
                (color: string) => <GridDesignIcon fill={color} />,
                t("configurations")
              )
            } satisfies NavGroupChild
          ]
        : []),
      {
        text: t("status"),
        to: "/service-support/status",
        match: (p: string) => p === "/service-support/status",
        icon: createNavDrawerDefaultIconRenderer(
          () => <LiveData20Px />,
          t("status")
        ),
        altIcon: createNavDrawerAltIconRenderer(
          (color: string) => <LiveData20Px fill={color} />,
          t("status")
        )
      },
      ...(shouldRenderTasksTab
        ? [
            {
              text: t("Tasks"),
              to: "/service-support/tasks",
              match: (p: string) => p === "/service-support/tasks",
              icon: createNavDrawerDefaultIconRenderer(
                () => <Requirements24Px />,
                t("Tasks")
              ),
              altIcon: createNavDrawerAltIconRenderer(
                (color: string) => <Requirements24Px fill={color} />,
                t("Tasks")
              )
            } satisfies NavGroupChild
          ]
        : []),
      ...(shouldDisplayAndonTab
        ? [
            {
              text: t("andon"),
              to: "/service-support/andon",
              match: (p: string) => p === "/service-support/andon",
              icon: createNavDrawerDefaultIconRenderer(
                () => <Attention20Px />,
                t("andon")
              ),
              altIcon: createNavDrawerAltIconRenderer(
                (color: string) => <Attention20Px fill={color} />,
                t("andon")
              )
            } satisfies NavGroupChild,
            {
              text: t("events"),
              to: "/service-support/events",
              match: (p: string) => p === "/service-support/events",
              icon: createNavDrawerDefaultIconRenderer(
                () => <Switch20Px />,
                t("events")
              ),
              altIcon: createNavDrawerAltIconRenderer(
                (color: string) => <Switch20Px fill={color} />,
                t("events")
              )
            } satisfies NavGroupChild
          ]
        : []),
      ...(enableTestingToolsTab
        ? [
            {
              text: t("testing tools"),
              to: "/service-support/testing-tools",
              match: (p: string) => p === "/service-support/testing-tools",
              icon: createNavDrawerDefaultIconRenderer(
                () => <Consol20Px />,
                t("testing tools")
              ),
              altIcon: createNavDrawerAltIconRenderer(
                (color: string) => <Consol20Px fill={color} />,
                t("testing tools")
              )
            } satisfies NavGroupChild
          ]
        : [])
    ];
  }, [
    enableSideNavSubMenus,
    shouldRenderTasksTab,
    shouldDisplayAndonTab,
    shouldRenderConfigurationsTab,
    enableTestingToolsTab,
    t
  ]);

  const serviceSupportDrawerDecorated = decorateNavDrawerLinkIcons({
    text: t("service and support"),
    to: "/service-support",
    icon: () => <Service20Px />,
    altIcon: (color: string) => <Service20Px fill={color} />
  });

  const getShouldShowConfirmModal = async () => {
    let shouldShowConfirmModal = false;
    if (!siteWorkstation) return shouldShowConfirmModal;
    await Promise.all(
      workstationAvailablePorts.map((port) =>
        dispatch(fetchPortStatus({ portId: port }))
      )
    ).then((statuses) => {
      shouldShowConfirmModal = statuses.some(
        (status) =>
          status && (status.selectedBin > 0 || status.selectedTask > 0)
      );
    });
    return shouldShowConfirmModal;
  };

  const handleNavigate = (toUrl: string, e?: SyntheticEvent): void => {
    // if on workstation bin maintenance page, do nothing if navigating to same page
    if (
      toUrl.includes("workstation-bin-maintenance") &&
      currentPagePathname === "workstation-bin-maintenance"
    ) {
      setIsSideNavOpen(false);
      return;
    }
    mixpanelTrackNavigationDrawerWrapper(toUrl, trackedPageName);
    // cleanup workstation on any off-page navigation
    if (
      (isAutostoreView(search) || pathname.includes("autostore")) &&
      selectedAutostoreGridId &&
      sitePortId
    ) {
      if (e) e.preventDefault();
      fetchAutostoreGridStatus(selectedAutostoreGridId)
        .then(async ({ data: systemStatus }) => {
          if (systemStatus?.kind === "error") {
            navigate(toUrl);
          } else {
            if (await getShouldShowConfirmModal()) {
              setNavigateToUrl(toUrl);
              dispatch(setConfirmationModalVisibilityStatus(true));
              setIsSideNavOpen(false);
            } else {
              setIsSideNavOpen(false);
              navigate(toUrl);
            }
          }
        })
        .catch(() => {
          navigate(toUrl);
        });
    } else if (
      // if on bin-maintenance screen or picking previous bin screen,
      // with selected grid & port, open navigate away confirmation modal
      (currentPagePathname === "bin-maintenance" ||
        currentPagePathname === "manage-flagged" ||
        currentPagePathname === "picking-previous-bin") &&
      selectedAutostoreGridId &&
      sitePortId
    ) {
      if (e) e.preventDefault();
      setNavigateToUrl(toUrl);
      dispatch(setConfirmationModalVisibilityStatus(true));
      setIsSideNavOpen(false);
    } else {
      // if not on autostore screen, navigate away without confirmation
      if (e) e.preventDefault();
      setIsSideNavOpen(false);
      navigate(toUrl);
    }
  };

  const confirmNavigateAway = async () => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Confirm"
    });
    dispatch(setConfirmationModalVisibilityStatus(false));
    const isMultiportWorkstation = parentPortIdFoundInWorkstation(
      siteWorkstation?.ports
    );
    const isSinglePortWorkstation =
      isWorkstationWithSinglePort(siteWorkstation);

    if (isMultiportWorkstation || isSinglePortWorkstation) {
      await dispatch(closePort());
    } else {
      await dispatch(closeWorkstation());
    }
    if (isAdjustingBins) {
      dispatch(setIsAdjustingBins(false));
      dispatch(setSelectedSummaries([]));
    }
    if (navigateToUrl.includes("logout")) {
      navigate("/logout");
    } else navigate(navigateToUrl);
  };

  const showAutostoreLink =
    usersFulfillmentCenter?.pickingConfigurations.includes("Autostore") &&
    !isSelectedBinCleaningWorkstation;

  // always show batch links for now
  const showBatchLinks =
    usersFulfillmentCenter?.pickingConfigurations.includes("Manual") ||
    usersFulfillmentCenter?.pickingConfigurations.includes("Autostore");

  const showCreateBatchLink =
    showBatchLinks && usersFulfillmentCenter
      ? usersFulfillmentCenter.ecommerceConfiguration === "WmsAndEcommerce"
      : false;

  const showCartLink =
    usersFulfillmentCenter?.waveCompletionConfiguration === "TotesVerified";

  const { hasMultipleBinConfigurationTypes } = useGetBinConfigurationsQuery(
    undefined,
    {
      skip: !usersClientId || !usersFulfillmentCenter,
      selectFromResult: ({ data }) => ({
        hasMultipleBinConfigurationTypes: data && data.length > 1
      })
    }
  );

  const navDrawerEntries: NavDrawerEntry[] = [
    ...(manualOpsEnabled
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.link.manual ops"),
              to: "/",
              icon: () => <Warehouse20Px />,
              altIcon: (color: string) => <Warehouse20Px fill={color} />,
              additionalRoutes: [
                "/putaway",
                "manual-inventory/product",
                "manual-inventory/bin",
                "inventory-holds",
                "cycle-count",
                "pick-batches",
                "cart-prep",
                "ship"
              ]
            })
          }
        ]
      : []),
    ...(showAutostoreLink
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.link.autostore"),
              to: "/autostore-main",
              icon: () => <R5Robot24Px />,
              altIcon: (color: string) => <R5Robot24Px fill={color} />,
              additionalRoutes: ["autostore"]
            })
          }
        ]
      : []),
    {
      entryType: navDrawerEntryType.link,
      ...decorateNavDrawerLinkIcons({
        text: t("nav.link.orders"),
        to: "/orders",
        icon: () => <Requirements24Px />,
        altIcon: (color: string) => <Requirements24Px fill={color} />
      })
    },
    ...(showBatchLinks
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.link.batches"),
              to: "/admin-batches",
              icon: () => <Batch24Px />,
              altIcon: () => <AltBatchIcon />
            })
          }
        ]
      : []),
    ...(showCreateBatchLink
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.link.create batches"),
              to: "/create-batches",
              icon: () => <BatchAdd24Px />,
              altIcon: () => <AltBatchAddIcon />
            })
          }
        ]
      : []),
    ...(showCartLink
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.link.carts"),
              to: "/carts",
              icon: () => <Cart20Px />,
              altIcon: (color: string) => <Cart20Px fill={color} />
            })
          }
        ]
      : []),
    {
      entryType: navDrawerEntryType.link,
      ...decorateNavDrawerLinkIcons({
        text: t("nav.link.analytics"),
        to: "/analytics",
        icon: () => <ChartBar24Px />,
        altIcon: (color: string) => <ChartBar24Px fill={color} />
      })
    },
    ...(binCleaningEnabled &&
    isSelectedBinCleaningWorkstation &&
    !enableWorkstationBinMaintenance
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.viewname.bin maintenance"),
              to: "/bin-maintenance",
              icon: () => <Bin24Px />,
              altIcon: (color: string) => <Bin24Px fill={color} />
            })
          }
        ]
      : []),
    ...(enableWorkstationBinMaintenance && isMaintenanceWorkstation
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.viewname.bin maintenance"),
              to: "/workstation-bin-maintenance?autostore=true",
              icon: () => <Bin24Px />,
              altIcon: (color: string) => <Bin24Px fill={color} />,
              additionalRoutes: ["workstation-bin-maintenance"]
            })
          },
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.viewname.escalated bins"),
              to: "/escalated-bins?autostore=true",
              icon: () => <Attention20Px />,
              altIcon: (color: string) => <Attention20Px fill={color} />,
              additionalRoutes: ["escalated-bins"]
            })
          }
        ]
      : []),
    ...(hasMultipleBinConfigurationTypes
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...decorateNavDrawerLinkIcons({
              text: t("nav.viewname.bin reconfiguration"),
              to: "/start-bin-reconfiguration",
              icon: () => <GridDesignIcon />,
              altIcon: (color: string) => <GridDesignIcon fill={color} />
            })
          }
        ]
      : []),
    ...(isAdmin
      ? [
          enableSideNavSubMenus
            ? ({
                entryType: navDrawerEntryType.group,
                id: "serviceSupport",
                text: serviceSupportDrawerDecorated.text,
                icon: serviceSupportDrawerDecorated.icon,
                altIcon: serviceSupportDrawerDecorated.altIcon,
                children: serviceSupportSubNavChildren
              } satisfies NavGroup)
            : {
                entryType: navDrawerEntryType.link,
                ...serviceSupportDrawerDecorated
              }
        ]
      : []),
    ...(canUserViewSettings
      ? [
          enableSideNavSubMenus
            ? ({
                entryType: navDrawerEntryType.group,
                id: "settings",
                text: settingsDrawerDecorated.text,
                icon: settingsDrawerDecorated.icon,
                altIcon: settingsDrawerDecorated.altIcon,
                children: settingsSubNavChildren
              } satisfies NavGroup)
            : {
                entryType: navDrawerEntryType.link,
                ...settingsDrawerDecorated
              }
        ]
      : []),
    ...(showDevSection
      ? [
          {
            entryType: navDrawerEntryType.link,
            ...devToolsDrawerDecorated
          }
        ]
      : [])
  ];

  const isNavGroupOpen = (id: NavGroup["id"], children: NavGroupChild[]) => {
    if (groupExpanded[id] === false) return false;
    if (groupExpanded[id] === true) return true;
    return children.some((navChild) => navChild.match(pathname, search));
  };

  const navDrawerGroupIds = navDrawerEntries
    .filter((e): e is NavGroup => e.entryType === navDrawerEntryType.group)
    .map((e) => e.id);

  return (
    <>
      <Drawer
        anchor="left"
        variant="temporary"
        open={isSideNavOpen}
        onClose={(): void => {
          setIsSideNavOpen(!isSideNavOpen);
        }}
        sx={{
          "& .MuiDrawer-paper": {
            top: navBarHeight,
            bottom: `-${navBarHeight}`,
            height: `calc(100% - ${navBarHeight})`,
            width: drawerWidth
          }
        }}
      >
        <div aria-label="Drawer" role="presentation">
          <List>
            {navDrawerEntries.map((navDrawerEntry) => {
              if (navDrawerEntry.entryType === navDrawerEntryType.group) {
                const open = isNavGroupOpen(
                  navDrawerEntry.id,
                  navDrawerEntry.children
                );
                const anyChildSelected = navDrawerEntry.children.some(
                  (navChild) => navChild.match(pathname, search)
                );
                const settingsSectionWithoutNavChild =
                  navDrawerEntry.id === "settings" &&
                  (pathname.includes("order-creation") ||
                    pathname === "/settings/api-tokens");
                const parentSelected =
                  anyChildSelected || settingsSectionWithoutNavChild;

                const groupSubmenuId = `side-nav-submenu-${navDrawerEntry.id}`;
                const groupTriggerId = `side-nav-trigger-${navDrawerEntry.id}`;

                return (
                  <Box key={navDrawerEntry.id}>
                    <ListItemButton
                      id={groupTriggerId}
                      aria-expanded={open}
                      aria-controls={groupSubmenuId}
                      onClick={() => {
                        const id = navDrawerEntry.id;
                        setGroupExpanded((prev) => {
                          if (open) {
                            return { ...prev, [id]: false };
                          }
                          const next = { ...prev };
                          for (const gid of navDrawerGroupIds) {
                            next[gid] = gid === id;
                          }
                          return next;
                        });
                      }}
                      sx={{
                        backgroundColor: parentSelected
                          ? "primary.light"
                          : "inherit",
                        "&:hover": {
                          backgroundColor: parentSelected
                            ? "primary.light"
                            : "inherit"
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          display: "flex",
                          justifyContent: "center"
                        }}
                      >
                        {parentSelected
                          ? navDrawerEntry.altIcon("#185BD1")
                          : navDrawerEntry.icon()}
                      </Box>
                      <ListItemText
                        primary={navDrawerEntry.text}
                        sx={{
                          fontSize: 16,
                          padding: "0 14px",
                          flex: "1 1 auto",
                          minWidth: 0,
                          color: parentSelected
                            ? "primary.dark"
                            : "text.primary"
                        }}
                      />
                      {open ? (
                        <ExpandLess sx={{ color: "text.secondary" }} />
                      ) : (
                        <ExpandMore sx={{ color: "text.secondary" }} />
                      )}
                    </ListItemButton>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                      <List
                        id={groupSubmenuId}
                        component="div"
                        disablePadding
                        role="group"
                        aria-labelledby={groupTriggerId}
                      >
                        {navDrawerEntry.children.map((child) => {
                          const childSelected = child.match(pathname, search);
                          return (
                            <RouterLink
                              key={`${navDrawerEntry.id}-${child.to}`}
                              to={child.to}
                              style={{
                                color: "black",
                                textDecoration: "none",
                                display: "block",
                                width: "100%"
                              }}
                              onClick={(e) => handleNavigate(child.to, e)}
                            >
                              <ListItemButton
                                sx={(theme) => ({
                                  pl: `calc(${theme.spacing(2)} + ${theme.spacing(2)})`,
                                  pr: 2,
                                  py: 1,
                                  borderLeft: `2px solid ${theme.palette.divider}`,
                                  backgroundColor: childSelected
                                    ? "primary.light"
                                    : "inherit",
                                  "&:hover": {
                                    backgroundColor: childSelected
                                      ? "primary.light"
                                      : "inherit"
                                  }
                                })}
                              >
                                <Box
                                  sx={{
                                    width: "40px",
                                    display: "flex",
                                    justifyContent: "center"
                                  }}
                                >
                                  {childSelected
                                    ? child.altIcon("#185BD1")
                                    : child.icon()}
                                </Box>
                                <ListItemText
                                  primary={child.text}
                                  sx={{
                                    fontSize: 16,
                                    padding: "0 14px",
                                    color: childSelected
                                      ? "primary.dark"
                                      : "text.primary"
                                  }}
                                />
                              </ListItemButton>
                            </RouterLink>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              if (navDrawerEntry.entryType !== navDrawerEntryType.link) {
                return null;
              }
              let isSelected: boolean;
              if (
                navDrawerEntry.to === "/autostore-main" &&
                isAutostoreQueryParamPresent
              ) {
                isSelected = true;
              } else if (
                navDrawerEntry.to === "/" &&
                !isAutostoreQueryParamPresent
              ) {
                isSelected =
                  pathname === navDrawerEntry.to ||
                  navDrawerEntry.additionalRoutes?.some((route) =>
                    pathname.includes(route)
                  ) ||
                  false;
              } else {
                isSelected =
                  pathnameMatchesNavLinkTarget(pathname, navDrawerEntry.to) ||
                  navDrawerEntry.additionalRoutes?.some((route) =>
                    pathname.includes(route)
                  ) ||
                  false;
              }
              return (
                <RouterLink
                  key={navDrawerEntry.text}
                  to={navDrawerEntry.to}
                  style={{ color: "black", textDecoration: "none" }}
                  onClick={(e) => handleNavigate(navDrawerEntry.to, e)}
                >
                  <ListItemButton
                    sx={{
                      backgroundColor: isSelected ? "primary.light" : "inherit",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? "primary.light"
                          : "inherit"
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: "40px",
                        display: "flex",
                        justifyContent: "center"
                      }}
                    >
                      {isSelected
                        ? navDrawerEntry.altIcon("#185BD1")
                        : navDrawerEntry.icon()}
                    </Box>
                    <ListItemText
                      primary={navDrawerEntry.text}
                      sx={{
                        fontSize: 16,
                        padding: "0 14px",
                        color: isSelected ? "primary.dark" : "text.primary"
                      }}
                    />
                  </ListItemButton>
                </RouterLink>
              );
            })}
          </List>
        </div>
      </Drawer>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        labelledById="navigate-away-modal-title"
        confirmCb={confirmNavigateAway}
        closeCb={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Cancel"
          });
          dispatch(setConfirmationModalVisibilityStatus(false));
          if (
            isAutostorePicking &&
            usersFulfillmentCenter?.pickQuantityConfirmationEnabled
          ) {
            dispatch(
              setPickedQuantityModalStatus({
                isShown: false,
                pickedQuantity: null,
                selectedBinId: null
              })
            );
            // next PR
            // setActivePickingModal(null);
          }
        }}
        modalTitle={t("are you sure you want to navigate out of the page")}
        modalText=""
      />
    </>
  );
};
